import { Helmet } from "react-helmet";
import { Box } from "@mui/material";
import {
  Alert,
  Button,
  Card,
  ClickAwayListener,
  Container,
  Grid,
  MenuItem, MenuList, Paper, Popper,
  TableCell,
  TextField,
  Tooltip,
  Typography
} from "@material-ui/core";
import { makeStyles } from "@material-ui/styles";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import DataTable from "../../../../components/DataTable";

const style = (theme) => ({
  paper: {
    backgroundColor: "#3A4D54",
    color: theme.palette.common.white,
    borderRadius: "4px"
  },
  boxSubmenu: {
    "&::before": {
      content: '""',
      position: "absolute",
      width: "0",
      height: "0",
      top: "8px",
      right: "-15px",
      border: "8px solid",
      borderColor: "transparent transparent transparent",
      borderLeftColor: "#3A4D54",
      zIndex: 1
    }
  },
  divider: {
    height: '70%',
    backgroundColor: theme.palette.divider,
    margin: theme.spacing(0, 1),
  },
  menuItem: {
    fontSize: "small",
    display: "block"
  },
});

const useStyles = makeStyles(style);

export default function BundlesPage({
  onCreateClick,
  onEnableClick,
  onDisableClick,
  onEditClick,
  onDuplicateClick,
  onDeleteClick,
  rows,
  totalRecords,
  page,
  rowsPerPage,
  isLoading,
  anchorEl,
  open,
  handleOpen,
  handleClose,
  selectedRow,
  onPageClick,
  enableItemPerPageOption,
  handleItemPerPageChange,
  canEdit,
  search,
  onSearchChange,
  hasFailedToLoad,
  onRetry,
}) {
  const classes = useStyles();

  return (
    <Box>
      <Helmet>Access Bundles | Kood Asia Management Portal</Helmet>
      <Box
        sx={{
          backgroundColor: 'background.default',
          minHeight: '100%',
          py: 3,
        }}
      >
        <Container maxWidth={false}>
          <Card
            sx={{
              mb: 2,
            }}
          >
            <Grid container justifyContent="space-between" alignItems="center">
              <Grid item xs>
                <Typography
                  variant="h4"
                  sx={{
                    padding: 2,
                  }}
                >
                  Access Bundles
                </Typography>
              </Grid>
              <Grid item>
                <Box
                  display="flex"
                  alignItems="center"
                  pr={2}
                  sx={{ gap: 2 }}
                >
                  <TextField
                    size="small"
                    variant="outlined"
                    label="Search"
                    value={search}
                    onChange={(event) => onSearchChange(event.target.value)}
                  />
                  {canEdit && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={onCreateClick}
                    >
                      Add Bundle
                    </Button>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
          {hasFailedToLoad ? (
            <Card sx={{ p: 3 }}>
              <Alert
                severity="error"
                action={(
                  <Button color="inherit" size="small" onClick={onRetry}>
                    Retry
                  </Button>
                )}
              >
                The bundles could not be loaded. Nothing has been changed — try again.
              </Alert>
            </Card>
          ) : (
          <DataTable
            isLoading={isLoading}
            rows={rows}
            rowsPerPage={rowsPerPage}
            total={totalRecords}
            page={page}
            renderHeader={() => (
              <>
                <TableCell>
                  Name
                </TableCell>
                {/* Code, Modules and Rights are deliberately not columns: the code is an
                    internal identifier, and the two counts are properties of the bundle's
                    contents rather than something to scan a list by. All three are on the
                    Edit dialog, where the modules themselves are on screen. */}
                <TableCell>
                  Companies
                </TableCell>
                <TableCell>
                  Status
                </TableCell>
                <TableCell>
                  Action
                </TableCell>
              </>
            )}
            renderRow={(row) => {
              const actionItems = [
                {
                  label: 'Edit',
                  onClick: () => onEditClick(row.id),
                },
                {
                  label: 'Duplicate',
                  condition: canEdit,
                  onClick: () => onDuplicateClick(row.id),
                },
                {
                  label: 'Activate',
                  condition: row.status === 0,
                  onClick: () => onEnableClick(row.id),
                },
                {
                  label: 'Deactivate',
                  condition: row.status === 1,
                  onClick: () => onDisableClick(row.id),
                },
                {
                  label: 'Delete',
                  // A bundle a company still holds cannot be deleted (the server 409s), so the
                  // row action is disabled with the reason rather than offering a confirm that
                  // can only fail.
                  disabled: row.companyAssigned > 0,
                  tooltip: row.companyAssigned > 0
                    ? `Assigned bundles cannot be deleted (${row.companyAssigned} companies)`
                    : "",
                  onClick: () => onDeleteClick(row),
                },
              ];

              return (
                <>
                  <TableCell>
                    {row.name}
                  </TableCell>
                  <TableCell>
                    {row.companyAssigned}
                  </TableCell>
                  <TableCell>
                    {row.status === 1 && <Box>Active</Box>}
                    {row.status === 0 && <Box>Inactive</Box>}
                  </TableCell>
                  <TableCell>
                    {canEdit && (
                      <>
                        <Button
                          onClick={(event) => {
                            handleOpen(event, row.id);
                          }}
                        >
                          <MoreHorizIcon />
                        </Button>
                        <Popper
                          open={open && selectedRow === row.id}
                          anchorEl={anchorEl}
                          onClose={handleClose}
                          placement="left-start"
                          style={{ zIndex: 1 }}
                        >
                          <Box className={classes.boxSubmenu}>
                            <Paper className={classes.paper}>
                              <ClickAwayListener onClickAway={handleClose}>
                                <MenuList>
                                  {actionItems
                                    .filter((item) => item.condition !== false)
                                    .map((item) => {
                                      const menuItem = (
                                        <MenuItem
                                          className={classes.menuItem}
                                          disabled={item.disabled}
                                          onClick={(e) => {
                                            item.onClick();
                                            handleClose(e);
                                          }}
                                        >
                                          {item.label}
                                        </MenuItem>
                                      );
                                      // A disabled MenuItem swallows pointer events, so the
                                      // Tooltip that explains WHY needs a live span wrapper.
                                      if (item.tooltip) {
                                        return (
                                          <Tooltip
                                            title={item.tooltip}
                                            key={`${row.id}.${item.label}`}
                                          >
                                            <span>{menuItem}</span>
                                          </Tooltip>
                                        );
                                      }
                                      return (
                                        <span key={`${row.id}.${item.label}`}>
                                          {menuItem}
                                        </span>
                                      );
                                    })}
                                </MenuList>
                              </ClickAwayListener>
                            </Paper>
                          </Box>
                        </Popper>
                      </>
                    )}
                  </TableCell>
                </>
              );
            }}
            onPageClick={onPageClick}
            enableItemPerPageOption={enableItemPerPageOption}
            handleItemPerPageChange={handleItemPerPageChange}
          />
          )}
        </Container>
      </Box>
    </Box>
  );
}
